import React, { useState, useCallback, useEffect } from 'react';
import { FeedPost } from '../types';
import { MomentCard } from '../components/MomentCard';
import { EmptyState } from '../components/EmptyState';
import { ErrorState } from '../components/ErrorState';
import { useAudio } from '../contexts/AudioContext';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useSession } from '../hooks/useSession';

interface Props {
  onNavigate: (page: string, params?: Record<string, string>) => void;
  onLoginRequired?: () => void;
}

export function FeedPage({ onNavigate, onLoginRequired }: Props) {
  const session = useSession();
  const currentUserId = session.status === 'authenticated' ? session.user.id : null;
  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [newPostsAvailable, setNewPostsAvailable] = useState(false);
  const newestPostIdRef = React.useRef<string | null>(null);
  const loadingRef = React.useRef(false);
  const loadMore = useCallback(async (cursor: string | null = null) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const url = cursor
        ? `/api/v1/posts/feed?cursor=${encodeURIComponent(cursor)}`
        : '/api/v1/posts/feed';
      const res = await fetch(url);
      if (res.status === 401) { window.location.href = '/'; return; }
      if (!res.ok) throw new Error('Failed to load');
      const json = await res.json() as { data: FeedPost[]; next_cursor: string | null };
      setPosts(prev => cursor ? [...prev, ...json.data] : json.data);
      setNextCursor(json.next_cursor);
      setInitialized(true);
      if (!cursor && json.data.length > 0) {
        newestPostIdRef.current = json.data[0].id;
        setNewPostsAvailable(false);
      }
    } catch {
      setError('Could not load posts');
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMore(null); }, []); // eslint-disable-line

  // Poll for new posts every 45s
  useEffect(() => {
    const id = setInterval(async () => {
      if (!newestPostIdRef.current) return;
      try {
        const res = await fetch('/api/v1/posts/feed');
        if (!res.ok) return;
        const json = await res.json() as { data: { id: string }[] };
        if (json.data.length > 0 && json.data[0].id !== newestPostIdRef.current) {
          setNewPostsAvailable(true);
        }
      } catch { /* silent */ }
    }, 45000);
    return () => clearInterval(id);
  }, []);

  const handleLoadMore = useCallback(() => loadMore(nextCursor), [nextCursor, loadMore]);
  const sentinelRef = useInfiniteScroll(handleLoadMore, !!nextCursor && !loading);

  return (
    <div className="fade-in">
      {/* Sticky header */}
      <div style={stickyHeader}>
        <h1 style={logoStyle}>mylodic</h1>
        <VolumeControl />
      </div>

      {/* New posts banner */}
      {newPostsAvailable && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0', position: 'sticky', top: 57, zIndex: 9 }}>
          <button
            onClick={() => { loadingRef.current = false; loadMore(null); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 16px', borderRadius: 100,
              background: 'var(--accent)', color: '#fff',
              fontSize: 13, fontWeight: 600,
              boxShadow: '0 2px 12px rgba(255,77,77,0.4)',
              animation: 'fadeIn 0.2s ease',
            }}
          >
            ↑ New posts
          </button>
        </div>
      )}

      {/* Posts */}
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {posts.map(post => (
          <div key={post.id} style={{ borderBottom: '1px solid var(--border)', padding: '12px 16px' }}>
            <MomentCard
              post={post}
              currentUserId={currentUserId}
              onLoginRequired={onLoginRequired}
              onResonate={() => {}}
              onReply={p => {
                if (!currentUserId) { onLoginRequired?.(); return; }
                onNavigate('post', { reply_to: p.id });
              }}
              onOpen={p => onNavigate('post-detail', { postId: p.id })}
              onNavigateUser={u => onNavigate('user', { username: u })}
              onDelete={id => setPosts(prev => prev.filter(p => p.id !== id))}
              onUpdate={updated => setPosts(prev => prev.map(p => p.id === updated.id ? updated : p))}
            />
          </div>
        ))}

        {loading && <Spinner />}

        {error && <ErrorState message={error} onRetry={() => loadMore(null)} />}

        {initialized && posts.length === 0 && !loading && !error && (
          <EmptyState
            heading="Nothing here yet"
            body="Follow people or post your first moment."
          />
        )}

        <div ref={sentinelRef} style={{ height: 1 }} />
      </div>
    </div>
  );
}

function VolumeControl() {
  const { volume, setVolume } = useAudio();
  const onChange = setVolume;
  const muted = volume === 0;
  const prevVolume = React.useRef(0.8);

  function handleMuteToggle() {
    if (muted) {
      onChange(prevVolume.current || 0.8);
    } else {
      prevVolume.current = volume;
      onChange(0);
    }
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <button
        onClick={handleMuteToggle}
        style={{ color: muted ? 'var(--text-muted)' : 'var(--text)', display: 'flex', flexShrink: 0, padding: 2 }}
        aria-label={muted ? 'Unmute' : 'Mute'}
      >
        {muted ? (
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11,5 6,9 2,9 2,15 6,15 11,19" />
            <line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" />
          </svg>
        ) : volume < 0.5 ? (
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11,5 6,9 2,9 2,15 6,15 11,19" />
            <path d="M15.54 8.46a5 5 0 010 7.07" />
          </svg>
        ) : (
          <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11,5 6,9 2,9 2,15 6,15 11,19" />
            <path d="M15.54 8.46a5 5 0 010 7.07" />
            <path d="M19.07 4.93a10 10 0 010 14.14" />
          </svg>
        )}
      </button>

      <input
        type="range" min={0} max={1} step={0.01} value={volume}
        onChange={e => onChange(Number(e.target.value))}
        className="vol-slider"
        style={{
          background: `linear-gradient(to right, var(--accent) ${volume * 100}%, var(--border) ${volume * 100}%)`,
        }}
        aria-label="Volume"
      />
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 6, padding: 28 }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }} />
      ))}
    </div>
  );
}

const stickyHeader: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '12px 16px',
  position: 'sticky', top: 0,
  background: 'rgba(10,10,10,0.92)',
  backdropFilter: 'blur(12px)',
  borderBottom: '1px solid var(--border)',
  zIndex: 10,
};

const logoStyle: React.CSSProperties = {
  fontSize: 20, fontWeight: 800, letterSpacing: '-0.03em',
  color: '#ff4d4d',
};
